import LoadingSpinner from '@/components/LoadingSpinner'
import {
  LAG,
  Port,
  Service,
  ServiceComponent,
} from '@/lib/definitions/api.types'
import { ProductIdentifiers } from '@/lib/definitions/types'
import { useApp } from '@/lib/provider/AppProvider'
import { ServiceService } from '@/lib/services/ServiceService'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import DynamicProduct from '../ServiceWizard/DynamicProduct'

const serviceService = new ServiceService()

const ServiceDetailsPageViaServiceId = () => {
  const navigate = useNavigate()
  const [appState] = useApp()
  const { serviceId = '' } = useParams()
  const [isError, setIsError] = useState(false)
  const [dynamicProductId, setDynamicProductId] = useState('')

  const goTo = (path: string) => {
    navigate(path, {
      replace: true,
    })
  }

  const redirectToServiceComponentDetails = (
    serviceComponent: ServiceComponent | LAG | Port
  ) => {
    if (serviceComponent.response_type === 'LAG') {
      return goTo(`/services/lag/${serviceComponent.id}`)
    }
    if (serviceComponent.response_type === 'Port') {
      return goTo(`/services/port/${serviceComponent.id}`)
    }
  }

  const redirectToServiceDetails = (service: Service) => {
    if (!service) return
    switch (service.product.pid) {
      case ProductIdentifiers.IP_TRANSIT:
      case ProductIdentifiers.IP_TRANSIT_OVER_GRE:
        return goTo(`/services/ip-transit/${service.id}`)
      case ProductIdentifiers.IP_ACCESS:
        return goTo(`/services/ip-access/${service.id}`)
      case ProductIdentifiers.FLEX_ETHERNET_ENDPOINT:
      case ProductIdentifiers.LAYER2:
        return goTo(`/services/flex-ethernet/${service.id}`)
      case ProductIdentifiers.DDOS_PROTECTION:
      case ProductIdentifiers.SE_DDOS_PROTECTION:
        return goTo(`/services/ddos-protection/${service.id}`)
      case ProductIdentifiers.EXTENSION:
        return goTo(`/services/extensions/${service.id}`)
      case ProductIdentifiers.CLOUD_CONNECT:
        return goTo(`/services/cloud-connect/${service.id}`)
      default:
        console.warn('Unknown service type:', service.response_type) // eslint-disable-line
        setIsError(true)
    }
  }

  useEffect(() => {
    const serviceIdCleaned = serviceId?.toLowerCase()

    // check if sco/sid param is present
    const searchParams = new URLSearchParams(window.location.search)
    const scoParam = searchParams.get('sco')
    const sidParam = searchParams.get('sid')
    if (scoParam) {
      serviceService.getServiceComponent(scoParam).then((serviceComponent) => {
        redirectToServiceComponentDetails(serviceComponent.data)
      })
      return
    }
    if (sidParam) {
      serviceService.getService(sidParam).then((service) => {
        redirectToServiceDetails(service.data)
      })
      return
    }

    if (/^sco\d+$/.test(serviceIdCleaned)) {
      serviceService.getServiceComponent(serviceId).then((serviceComponent) => {
        redirectToServiceComponentDetails(serviceComponent.data)
      })
      return
    }
    if (/^sid\d+$/.test(serviceIdCleaned)) {
      serviceService.getService(serviceId).then((service) => {
        redirectToServiceDetails(service.data)
      })
      return
    }
    if (serviceId && !Number.isNaN(Number(serviceId))) {
      serviceService.getService(serviceId).then((service) => {
        if (!service.data) {
          setIsError(true)
        } else {
          redirectToServiceDetails(service.data)
        }
      })
    }

    if (!serviceId || !appState.appInfo?.products) return
    const serviceIds =
      appState.appInfo.products.map((product: any) =>
        product.product_key.toLowerCase()
      ) || []
    const serviceIdWithoutHyphens = serviceIdCleaned.replace(/-/g, '_')
    if (serviceIds.includes(serviceIdWithoutHyphens)) {
      setDynamicProductId(serviceIdWithoutHyphens)
    } else {
      setIsError(true)
    }
  }, [serviceId, appState.appInfo?.products])

  if (dynamicProductId) {
    return <DynamicProduct productId={dynamicProductId} />
  }

  return isError ? (
    <div className="flex items-center justify-center h-screen">
      <section className="body-font flex justify-center items-center py-24">
        <div className="mx-auto flex px-5 py-24 items-center justify-center flex-col">
          <div className="flex items-center text-7xl font-bold">
            <span className="text-status-error-700">4</span>
            <span className="text-status-error-700">0</span>
            <span className="text-status-error-700">4</span>
          </div>
          <div className="text-center w-full">
            <h1 className="text-4xl mb-4 font-bold text-neutral-90">
              Service not found
            </h1>
            <p className="mb-8">
              The service you are looking for does not exist.
            </p>
          </div>
        </div>
      </section>
    </div>
  ) : (
    <div className="flex items-center justify-center h-screen">
      <LoadingSpinner className="size-18" />
    </div>
  )
}

export default ServiceDetailsPageViaServiceId
